// src/chatGPT/components/MessageWithArtifacts.jsx
import React, { memo, useMemo } from "react";
import ImprovedMarkdownRenderer from "./ImprovedMarkdownRenderer";
import ArtifactRenderer from "../../components/ArtifactRenderer";

const MessageWithArtifacts = memo(function MessageWithArtifacts({ content, className }) {
  // Parse artifacts from content with progressive loading support
  const parseContent = (text) => {
    const parts = [];
    
    // Regex for complete artifacts
    const completeArtifactRegex = /<artifact\s+identifier="([^"]+)"\s+type="([^"]+)"\s+title="([^"]+)"[^>]*>([\s\S]*?)(?=<\/artifact>)<\/artifact>/g;
    
    // Regex for incomplete artifacts (opening tag only)
    const openArtifactRegex = /<artifact\s+identifier="([^"]+)"\s+type="([^"]+)"\s+title="([^"]+)"[^>]*>\s*$/;
    
    let lastIndex = 0;
    let match;
    const maxIterations = 1000; // Prevent infinite loops
    let iterations = 0;
    
    // First, find all complete artifacts
    while ((match = completeArtifactRegex.exec(text)) !== null && iterations < maxIterations) {
      iterations++;
      
      // Add text before artifact
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: text.slice(lastIndex, match.index)
        });
      }
      
      // Extract and validate artifact components
      const [fullMatch, identifier, type, title, dataStr] = match;
      
      // Validate required fields
      if (!identifier || !type || !title) {
        // Invalid artifact: missing required fields
        parts.push({
          type: 'error',
          content: `Invalid artifact: missing required fields`
        });
        lastIndex = match.index + fullMatch.length;
        continue;
      }
      
      // Parse artifact data with better error handling
      let artifactData;
      const trimmedData = dataStr.trim();

      // For markdown/text types, use raw string content (not JSON)
      const isTextType = type === 'text/markdown' || type === 'markdown' || type === 'text/plain';

      if (!trimmedData) {
        artifactData = isTextType ? '' : {};
      } else if (isTextType) {
        // For text/markdown, pass the raw string content directly
        artifactData = trimmedData;
      } else {
        try {
          // Handle potential JSON edge cases
          artifactData = JSON.parse(trimmedData);
        } catch (e) {
          // Failed to parse artifact data

          // Try to show a useful error message
          parts.push({
            type: 'error',
            content: `Failed to parse artifact "${title}": Invalid JSON data`
          });
          lastIndex = match.index + fullMatch.length;
          continue;
        }
      }
      
      // Add successfully parsed artifact
      parts.push({
        type: 'artifact',
        content: {
          identifier,
          type,
          title,
          data: artifactData
        }
      });
      
      lastIndex = match.index + fullMatch.length;
    }
    
    // Check for incomplete artifact at the end
    const remainingText = text.slice(lastIndex);
    const openMatch = openArtifactRegex.exec(remainingText);
    
    if (openMatch) {
      // Add text before the incomplete artifact
      if (openMatch.index > 0) {
        parts.push({
          type: 'text',
          content: remainingText.slice(0, openMatch.index)
        });
      }
      
      // Add pending artifact (skeleton)
      const [, identifier, type, title] = openMatch;
      parts.push({
        type: 'artifact-pending',
        content: {
          identifier,
          type,
          title,
          data: null
        }
      });
      
      // Add any partial JSON as faint text (optional)
      const partialJson = remainingText.slice(openMatch.index + openMatch[0].length).trim();
      if (partialJson) {
        parts.push({
          type: 'text',
          content: `\n\`\`\`json\n${partialJson}\n\`\`\`\n`
        });
      }
    } else if (remainingText) {
      // No pending artifact, just add remaining text
      parts.push({
        type: 'text',
        content: remainingText
      });
    }
    
    return parts;
  };
  
  const parts = useMemo(() => parseContent(content), [content]);

  return (
    <div className={className}>
      {parts.map((part, index) => {
        if (part.type === 'text') {
          return (
            <ImprovedMarkdownRenderer key={index}>
              {part.content}
            </ImprovedMarkdownRenderer>
          );
        } else if (part.type === 'error') {
          return (
            <div key={index} className="my-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <strong>⚠️ Artifact Error:</strong> {part.content}
            </div>
          );
        } else if (part.type === 'artifact-pending') {
          const pending = part.content;
          // Show a loading skeleton for pending artifacts
          return (
            <div key={index} className="my-6 border-2 border-gray-200 rounded-lg overflow-hidden animate-pulse">
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-lg">⏳</span>
                  <h3 className="font-semibold text-gray-600">{pending.title}</h3>
                </div>
              </div>
              <div className="bg-white p-4">
                <div className="h-32 bg-gray-100 rounded animate-pulse"></div>
              </div>
            </div>
          );
        } else {
          const artifact = part.content;
          return (
            <ArtifactRenderer
              key={index}
              artifact={{
                artType: artifact.type,
                title: artifact.title,
                data: artifact.data,
                identifier: artifact.identifier
              }}
            />
          );
        }
      })}
    </div>
  );
});

export default MessageWithArtifacts;